/**
 * Asking a view's own endpoint for a range of its rows.
 *
 * Three view types page identically -- gallery, list and gantt -- and a fourth,
 * the grid, differs only in reading `group_by_metadata` off the answer. What
 * they share is the whole of the request: `limit`/`offset` because the window is
 * a range of absolute row indexes and not a page number, `include=row_metadata`,
 * and the search when there is one.
 *
 * Filters and sortings are not sent. The endpoint applies the view's own,
 * server-side, which is why they appear in the query's signature but not in its
 * request parameters: the signature describes the answer, the parameters
 * describe the ask.
 *
 * Each view type gets its own module around this, holding its own path and its
 * own test seam -- see the note on `viewRowsFetcher` for why the seam cannot be
 * shared.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData } from '@/api/types'
import type { RowFetcher } from '../rows/rowFetcher'

/** The shape these endpoints answer with, narrowed to what is read. */
interface ViewRowsResponse {
  count?: number | null
  results?: unknown[]
}

/** The paths this can be pointed at, so a typo is a compile error. */
type ViewRowsPath =
  | '/api/database/views/gallery/{view_id}/'
  | '/api/database/views/list/{view_id}/'
  | '/api/database/views/gantt/{view_id}/'

/**
 * A fetcher and the seam that replaces it, for one view type's endpoint.
 *
 * The seam is per view type rather than shared because a board shows several
 * windows at once and a test has to be able to answer for each of them
 * differently; keeping the shape the same everywhere is what lets that stay
 * true as more view types arrive.
 *
 * The seam exists at all because under jsdom a real request carrying an abort
 * signal cannot be built: jsdom supplies an `AbortController` while `Request`
 * comes from Node, which refuses a signal that is not its own. Every request
 * here carries one, so a component test that reached the network would fail on
 * the environment rather than on the network.
 */
export function viewRowsFetcher(path: ViewRowsPath): {
  fetch: RowFetcher
  setForTests: (replacement: RowFetcher | null) => void
} {
  const real: RowFetcher = async (query, range, signal) => {
    const body = await unwrap<ViewRowsResponse>(
      getApi().GET(path, {
        params: {
          // A number by construction: these fetchers are only ever given a view
          // the reader is signed in to. A publicly shared view is addressed by
          // slug and has a fetcher of its own.
          path: { view_id: Number(query.viewId) },
          query: {
            limit: Math.max(0, range.end - range.start),
            offset: range.start,
            include: 'row_metadata',
            ...(query.search.term === ''
              ? {}
              : { search: query.search.term, search_mode: query.search.mode }),
          },
        },
        signal,
      }),
    )

    return {
      rows: (body.results ?? []) as RowData[],
      // Null and absent both mean "not told", which is not the same as zero.
      count: body.count ?? undefined,
    }
  }

  let current = real
  return {
    fetch: (query, range, signal) => current(query, range, signal),
    setForTests: (replacement) => {
      current = replacement ?? real
    },
  }
}
